/* eslint-disable prettier/prettier */
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Sidebar } from "primereact/sidebar";
import React, { useEffect, useMemo, useState } from "react";
import InvestorService from "services/invoice-service";
import { confirmDialog } from "primereact/confirmdialog";
import { setToaster } from "common/toastStore";

interface ViewInvestorProps {
  selectedInvestor: any;
  onSaveSuccess?: () => void;
  // hideInvestorModal: () => void;
}

interface IServicer {
  servicerName: string;
  productCodes: string[];
}

const normalizeValue = (value: string) => value?.trim().toLowerCase();

const deepCloneServicers = (rows: IServicer[] = []) =>
  rows.map((row) => ({
    servicerName: row.servicerName,
    productCodes: [...(row.productCodes || [])],
  }));

const VIewInvestorProdCodes: React.FC<ViewInvestorProps> = ({
  selectedInvestor,
  onSaveSuccess,
  // hideInvestorModal,
}) => {
  const [newServicerName, setNewServicerName] = useState("");
  const [newProductCode, setNewProductCode] = useState("");
  const [editingServicer, setEditingServicer] = useState<string | null>(null);
  const [inlineProductCode, setInlineProductCode] = useState("");
  const [editingCodeTarget, setEditingCodeTarget] = useState<{
    servicerName: string;
    originalCode: string;
  } | null>(null);
  const [editingCodeValue, setEditingCodeValue] = useState("");
  const [servicers, setServicers] = useState<IServicer[]>([]);
  const [initialServicers, setInitialServicers] = useState<IServicer[]>([]);
  const [showPendingChanges, setShowPendingChanges] = useState<boolean>(false);
  const [editingServicerNameTarget, setEditingServicerNameTarget] = useState<
    string | null
  >(null);
  const [editingServicerNameValue, setEditingServicerNameValue] =
    useState<string>("");

  const showWarningToast = (detail: string) => {
    setToaster({
      severity: "warn",
      summary: "Validation",
      detail,
    });
  };

  const normalizeServicerCodes = (input: any[] = []) => {
    return input.map((s: any) => {
      const rawCodes = Array.isArray(s?.productCode)
        ? s.productCode
        : Array.isArray(s?.ProductCode)
        ? s.ProductCode
        : Array.isArray(s?.productCodes)
        ? s.productCodes
        : [];

      const productCodes = rawCodes
        .map((codeItem: any) => {
          if (typeof codeItem === "string") return codeItem;
          return codeItem?.productCode || codeItem?.ProductCode || "";
        })
        .map((code: string) => code.trim())
        .filter(Boolean)
        .filter((code: string, index: number, arr: string[]) => {
          return (
            arr.findIndex((c) => normalizeValue(c) === normalizeValue(code)) ===
            index
          );
        });

      return {
        servicerName: s?.servicerName || s?.ServicerName || "",
        productCodes,
      };
    });
  };

  const getParsedUniqueCodes = (raw: string) => {
    const codes = raw
      .split(",")
      .map((c) => c.trim())
      .filter(Boolean);

    return codes.filter((code, index, arr) => {
      return (
        arr.findIndex((c) => normalizeValue(c) === normalizeValue(code)) ===
        index
      );
    });
  };

  const hasPendingChanges = useMemo(() => {
    return JSON.stringify(initialServicers) !== JSON.stringify(servicers);
  }, [initialServicers, servicers]);

  const pendingChangesSummary = useMemo(() => {
    const initialMap = new Map<string, IServicer>();
    const currentMap = new Map<string, IServicer>();

    initialServicers.forEach((s) =>
      initialMap.set(normalizeValue(s.servicerName), s),
    );
    servicers.forEach((s) => currentMap.set(normalizeValue(s.servicerName), s));

    const addedServicers: string[] = [];
    const removedServicers: string[] = [];
    const updatedServicers: Array<{
      servicerName: string;
      addedCodes: string[];
      removedCodes: string[];
    }> = [];

    servicers.forEach((servicer) => {
      const key = normalizeValue(servicer.servicerName);
      if (!initialMap.has(key)) {
        addedServicers.push(servicer.servicerName);
      }
    });

    initialServicers.forEach((servicer) => {
      const key = normalizeValue(servicer.servicerName);
      if (!currentMap.has(key)) {
        removedServicers.push(servicer.servicerName);
      }
    });

    servicers.forEach((currentServicer) => {
      const key = normalizeValue(currentServicer.servicerName);
      const initialServicer = initialMap.get(key);

      if (!initialServicer) return;

      const addedCodes = currentServicer.productCodes.filter(
        (code) =>
          !initialServicer.productCodes.some(
            (initialCode) =>
              normalizeValue(initialCode) === normalizeValue(code),
          ),
      );

      const removedCodes = initialServicer.productCodes.filter(
        (code) =>
          !currentServicer.productCodes.some(
            (currentCode) =>
              normalizeValue(currentCode) === normalizeValue(code),
          ),
      );

      if (addedCodes.length > 0 || removedCodes.length > 0) {
        updatedServicers.push({
          servicerName: currentServicer.servicerName,
          addedCodes,
          removedCodes,
        });
      }
    });

    const totalChangeCount =
      addedServicers.length +
      removedServicers.length +
      updatedServicers.reduce(
        (acc, item) => acc + item.addedCodes.length + item.removedCodes.length,
        0,
      );

    return {
      addedServicers,
      removedServicers,
      updatedServicers,
      totalChangeCount,
    };
  }, [initialServicers, servicers]);

  const updateInvestorsList = () => {
    const formattedServicers = servicers.map((s) => ({
      servicerName: s.servicerName,
      productCode: (s.productCodes || []).map((code: string) => ({
        productCode: code,
      })),
    }));

    const payloadBase: any = { ...(selectedInvestor || {}) };
    delete payloadBase.servicerProductCodes;
    const payload: any = {
      ...payloadBase,
      servicerProductCodes: formattedServicers,
    };

    InvestorService.updateInvestorsList(payload)
      .then(() => {
        setInitialServicers(deepCloneServicers(servicers));
        setShowPendingChanges(false);
        onSaveSuccess?.();
        setToaster({
          severity: "success",
          summary: "Success",
          detail: "Investor updated successfully!",
        });
        // hideInvestorModal();
      })
      .catch((e: Error) => {
        console.error("Update failed:", e);
        setToaster({
          severity: "error",
          summary: "Error",
          detail: "Failed to update investor.",
        });
      });
  };

  useEffect(() => {
    if (selectedInvestor?.servicerProductCodes) {
      const normalized = normalizeServicerCodes(
        selectedInvestor.servicerProductCodes,
      );

      setServicers(normalized);
      setInitialServicers(deepCloneServicers(normalized));
    } else {
      setServicers([]);
      setInitialServicers([]);
    }
  }, [selectedInvestor]);

  const addOrMergeServicer = () => {
    const name = newServicerName.trim();
    const incomingCodes = getParsedUniqueCodes(newProductCode);

    if (!name) {
      showWarningToast("Servicer name is required.");
      return;
    }

    if (!incomingCodes.length) {
      showWarningToast("At least one product code is required.");
      return;
    }

    const existingServicer = servicers.find(
      (s) => normalizeValue(s.servicerName) === normalizeValue(name),
    );

    if (!existingServicer) {
      setServicers([
        ...servicers,
        {
          servicerName: name,
          productCodes: incomingCodes,
        },
      ]);

      setNewServicerName("");
      setNewProductCode("");
      return;
    }

    const existingCodes = existingServicer.productCodes || [];
    const uniqueNewCodes = incomingCodes.filter(
      (code) =>
        !existingCodes.some(
          (existingCode) =>
            normalizeValue(existingCode) === normalizeValue(code),
        ),
    );

    if (!uniqueNewCodes.length) {
      showWarningToast("Product code already exists for this servicer.");
      return;
    }

    if (uniqueNewCodes.length !== incomingCodes.length) {
      setToaster({
        severity: "warn",
        summary: "Duplicate Product Code",
        detail: "Some product codes already existed and were skipped.",
      });
    }

    setServicers((prev) =>
      prev.map((s) => {
        if (normalizeValue(s.servicerName) !== normalizeValue(name)) return s;
        return {
          ...s,
          productCodes: [...existingCodes, ...uniqueNewCodes],
        };
      }),
    );

    setNewServicerName("");
    setNewProductCode("");
  };

  const handleDiscard = () => {
    const normalized = normalizeServicerCodes(
      selectedInvestor?.servicerProductCodes || [],
    );

    setServicers(normalized);
    setInitialServicers(deepCloneServicers(normalized));
    setShowPendingChanges(false);
    setEditingServicer(null);
    setInlineProductCode("");
    setEditingCodeTarget(null);
    setEditingCodeValue("");
  };

  return (
    <>
      <div className="mb-4">
        <div className="font-semibold mb-2">Add Servicer</div>
        <div className="flex items-start justify-between gap-2 w-full">
          <div className="flex flex-1 min-w-0 gap-2 items-start">
            <InputText
              placeholder="Servicer Name"
              value={newServicerName}
              onChange={(e) => setNewServicerName(e.target.value)}
            />
            <div className="flex-1">
              <InputTextarea
                placeholder="Product Code"
                value={newProductCode}
                onChange={(e) => setNewProductCode(e.target.value)}
                className="w-full"
                rows={2}
              />
              <small className="text-xs text-amber-700 font-semibold mt-1 block">
                Enter multiple product codes as comma-separated values.
              </small>
            </div>
            <Button
              label="Add"
              className="p-button-sm p-button-success p-button-outlined"
              onClick={addOrMergeServicer}
            />
          </div>

          <div className="flex shrink-0 justify-end gap-2">
            <Button
              label={
                hasPendingChanges
                  ? `View Pending Changes (${pendingChangesSummary.totalChangeCount})`
                  : "View Pending Changes"
              }
              icon="pi pi-list"
              className="p-button-sm p-button-outlined p-button-secondary"
              disabled={!hasPendingChanges}
              onClick={() => setShowPendingChanges(true)}
            />
            <Button
              label="Discard"
              icon="pi pi-times"
              className="p-button-sm p-button-danger p-button-outlined"
              onClick={handleDiscard}
            />
            <Button
              label="Save"
              disabled={servicers.length === 0 || !hasPendingChanges}
              onClick={() => {
                updateInvestorsList();
              }}
              icon="pi pi-check"
              className="p-button-sm p-button-success p-button-outlined"
            />
          </div>
        </div>
        {hasPendingChanges && (
          <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 shadow-sm">
            <div className="font-semibold mb-1">Unsaved changes notice</div>
            <div>
              Changes shown on this page are not saved until you click Save. If
              you leave the page, all unsaved changes will be lost.
            </div>
          </div>
        )}
      </div>

      {/* Service Configuration Table */}
      <DataTable value={servicers} stripedRows dataKey="servicerName">
        <Column
          header="Servicer Name"
          bodyClassName="align-top"
          body={(row) => {
            const isEditingName =
              editingServicerNameTarget === row.servicerName;

            return (
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  {isEditingName ? (
                    <div className="flex items-center gap-2 w-full">
                      <InputText
                        value={editingServicerNameValue}
                        onChange={(e) =>
                          setEditingServicerNameValue(e.target.value)
                        }
                        className="p-inputtext-sm w-full"
                      />
                      <Button
                        icon="pi pi-check"
                        style={{ padding: "4px" }}
                        className="p-button-text p-button-success"
                        onClick={() => {
                          const newName = editingServicerNameValue.trim();

                          if (!newName) {
                            showWarningToast("Servicer name is required.");
                            return;
                          }

                          const duplicate = servicers.some(
                            (s) =>
                              normalizeValue(s.servicerName) ===
                                normalizeValue(newName) &&
                              s.servicerName !== row.servicerName,
                          );

                          if (duplicate) {
                            showWarningToast("Servicer name already exists.");
                            return;
                          }

                          setServicers((prev) =>
                            prev.map((s) =>
                              s.servicerName === row.servicerName
                                ? { ...s, servicerName: newName }
                                : s,
                            ),
                          );

                          setEditingServicerNameTarget(null);
                          setEditingServicerNameValue("");
                        }}
                      />
                      <Button
                        icon="pi pi-times"
                        style={{ padding: "4px" }}
                        className="p-button-text p-button-secondary"
                        onClick={() => {
                          setEditingServicerNameTarget(null);
                          setEditingServicerNameValue("");
                        }}
                      />
                    </div>
                  ) : (
                    <>
                      <div className="text-base font-semibold text-gray-900">
                        {row.servicerName}
                      </div>
                      <Button
                        icon="pi pi-pencil"
                        style={{ padding: "4px" }}
                        className="p-button-text p-button-secondary"
                        onClick={() => {
                          setEditingServicerNameTarget(row.servicerName);
                          setEditingServicerNameValue(row.servicerName);
                          setEditingServicer(null);
                          setInlineProductCode("");
                        }}
                      />
                    </>
                  )}
                </div>

                <div className="text-xs uppercase tracking-wide text-gray-500">
                  {(row.productCodes || []).length} item
                  {(row.productCodes || []).length === 1 ? "" : "s"}
                </div>
              </div>
            );
          }}
        />

        <Column
          header="Product Codes"
          body={(row) => {
            const isEditing = editingServicer === row.servicerName;

            return (
              <div className="flex flex-col gap-2">
                <div style={{ minWidth: "240px" }}>
                  {(row.productCodes || []).length ? (
                    (row.productCodes || []).map(
                      (code: string, idx: number) => {
                        const isCodeEditing =
                          editingCodeTarget?.servicerName ===
                            row.servicerName &&
                          editingCodeTarget?.originalCode === code;

                        return (
                          <div
                            key={`${row.servicerName}-${code}-${idx}`}
                            className="flex items-center justify-between gap-2"
                            style={{ lineHeight: 1.8 }}
                          >
                            <div className="flex items-center gap-2 w-full">
                              <span className="w-8 text-xs text-gray-500 text-right">
                                {idx + 1}.
                              </span>
                              {isCodeEditing ? (
                                <InputText
                                  value={editingCodeValue}
                                  onChange={(e) =>
                                    setEditingCodeValue(e.target.value)
                                  }
                                  className="p-inputtext-sm w-full"
                                />
                              ) : (
                                <span>{code}</span>
                              )}
                            </div>

                            <div className="flex items-center gap-1">
                              {isCodeEditing ? (
                                <>
                                  <Button
                                    icon="pi pi-check"
                                    style={{
                                      padding: "2px",
                                      width: "1.25rem",
                                      height: "1.25rem",
                                    }}
                                    className="p-button-text p-button-success"
                                    onClick={() => {
                                      const updatedCode =
                                        editingCodeValue.trim();

                                      if (!updatedCode) {
                                        showWarningToast(
                                          "Product code cannot be empty.",
                                        );
                                        return;
                                      }

                                      const existingCodes = (
                                        row.productCodes || []
                                      ).filter(
                                        (c: string) =>
                                          c.toLowerCase() !==
                                          code.toLowerCase(),
                                      );

                                      const alreadyExists = existingCodes.some(
                                        (c: string) =>
                                          c.toLowerCase() ===
                                          updatedCode.toLowerCase(),
                                      );

                                      if (alreadyExists) {
                                        showWarningToast(
                                          "Product code already exists.",
                                        );
                                        return;
                                      }

                                      setServicers((prev) =>
                                        prev.map((s) => {
                                          if (
                                            s.servicerName !== row.servicerName
                                          )
                                            return s;
                                          return {
                                            ...s,
                                            productCodes: (
                                              s.productCodes || []
                                            ).map((c: string) =>
                                              normalizeValue(c) ===
                                              normalizeValue(code)
                                                ? updatedCode
                                                : c,
                                            ),
                                          };
                                        }),
                                      );

                                      setEditingCodeTarget(null);
                                      setEditingCodeValue("");
                                    }}
                                  />
                                  <Button
                                    icon="pi pi-times"
                                    style={{
                                      padding: "2px",
                                      width: "1.25rem",
                                      height: "1.25rem",
                                    }}
                                    className="p-button-text p-button-secondary"
                                    onClick={() => {
                                      setEditingCodeTarget(null);
                                      setEditingCodeValue("");
                                    }}
                                  />
                                </>
                              ) : (
                                <>
                                  <Button
                                    icon="pi pi-pencil"
                                    style={{
                                      padding: "2px",
                                      width: "1.25rem",
                                      height: "1.25rem",
                                    }}
                                    className="p-button-text p-button-secondary"
                                    onClick={() => {
                                      setEditingCodeTarget({
                                        servicerName: row.servicerName,
                                        originalCode: code,
                                      });
                                      setEditingCodeValue(code);
                                    }}
                                  />
                                  <Button
                                    icon="pi pi-times"
                                    style={{
                                      padding: "2px",
                                      width: "1.25rem",
                                      height: "1.25rem",
                                    }}
                                    className="p-button-text p-button-danger"
                                    onClick={() =>
                                      confirmDialog({
                                        message: `Delete product code "${code}" from "${row.servicerName}"?`,
                                        header: "Delete Confirmation",
                                        icon: "pi pi-exclamation-triangle",
                                        acceptClassName: "p-button-danger",
                                        accept: () => {
                                          setServicers((prev) =>
                                            prev.map((s) =>
                                              s.servicerName ===
                                              row.servicerName
                                                ? {
                                                    ...s,
                                                    productCodes:
                                                      s.productCodes.filter(
                                                        (c: string) =>
                                                          c !== code,
                                                      ),
                                                  }
                                                : s,
                                            ),
                                          );
                                        },
                                      })
                                    }
                                  />
                                </>
                              )}
                            </div>
                          </div>
                        );
                      },
                    )
                  ) : (
                    <span>-</span>
                  )}
                </div>

                {isEditing && (
                  <div className="w-full">
                    <div className="flex items-center gap-2 w-full">
                      <div className="flex-1">
                        <InputTextarea
                          value={inlineProductCode}
                          onChange={(e) => setInlineProductCode(e.target.value)}
                          placeholder="Product code"
                          className="p-inputtext-sm w-full"
                          rows={2}
                        />
                      </div>
                      <Button
                        icon="pi pi-check"
                        style={{ padding: "5px 3px", fontSize: 12 }}
                        className="p-button-sm p-button-success p-button-outlined"
                        onClick={() => {
                          const codes = getParsedUniqueCodes(inlineProductCode);

                          if (!codes.length) {
                            showWarningToast("Product code cannot be empty.");
                            return;
                          }

                          setServicers((prev) =>
                            prev.map((s) => {
                              if (s.servicerName !== row.servicerName) return s;

                              const existing = s.productCodes || [];

                              // normalize to avoid hidden duplicates
                              const existingSet = new Set(
                                existing.map((c: string) => c.toLowerCase()),
                              );

                              const uniqueNewCodes = codes.filter(
                                (c) => !existingSet.has(c.toLowerCase()),
                              );

                              if (!uniqueNewCodes.length) {
                                showWarningToast("Product code already exist.");
                                return s;
                              }

                              if (uniqueNewCodes.length !== codes.length) {
                                setToaster({
                                  severity: "warn",
                                  summary: "Duplicate Product Code",
                                  detail:
                                    "Some product codes already existed and were skipped.",
                                });
                              }

                              return {
                                ...s,
                                productCodes: [...existing, ...uniqueNewCodes],
                              };
                            }),
                          );

                          setEditingServicer(null);
                          setInlineProductCode("");
                        }}
                      />
                      <Button
                        icon="pi pi-times"
                        style={{ padding: "5px 3px", fontSize: 12 }}
                        className="p-button-sm p-button-danger p-button-outlined"
                        onClick={() => {
                          setEditingServicer(null);
                          setInlineProductCode("");
                        }}
                      />
                    </div>
                    <small className="text-xs text-amber-700 font-semibold mt-1 block">
                      Enter multiple product codes as comma-separated values.
                    </small>
                  </div>
                )}
              </div>
            );
          }}
        />

        <Column
          header="Actions"
          bodyClassName="align-top"
          body={(row) => {
            const isEditing = editingServicer === row.servicerName;

            return (
              <div className="flex items-start gap-2">
                {!isEditing && (
                  <Button
                    icon="pi pi-plus"
                    style={{ padding: "5px 3px", fontSize: 12 }}
                    className="p-button-sm p-button-secondary p-button-outlined"
                    onClick={() => {
                      setEditingServicer(row.servicerName);
                      setInlineProductCode("");
                    }}
                  />
                )}

                <Button
                  icon="pi pi-trash"
                  style={{ padding: "5px 3px", fontSize: 12 }}
                  className="p-button-sm p-button-danger p-button-outlined"
                  onClick={() =>
                    confirmDialog({
                      message: `Are you sure you want to delete servicer "${row.servicerName}"?`,
                      header: "Delete Confirmation",
                      icon: "pi pi-exclamation-triangle",
                      acceptClassName: "p-button-danger",
                      accept: () => {
                        setServicers((prev) =>
                          prev.filter(
                            (s) => s.servicerName !== row.servicerName,
                          ),
                        );
                      },
                    })
                  }
                />
              </div>
            );
          }}
        />
      </DataTable>

      <Sidebar
        visible={showPendingChanges}
        position="right"
        style={{ width: "36vw", minWidth: "360px" }}
        onHide={() => setShowPendingChanges(false)}
      >
        <div className="p-1">
          <h3 className="text-xl font-semibold mb-4">Pending Changes</h3>

          {pendingChangesSummary.totalChangeCount === 0 ? (
            <div className="text-sm text-gray-500">No pending changes.</div>
          ) : (
            <div className="space-y-4">
              {pendingChangesSummary.addedServicers.length > 0 && (
                <div className="rounded border border-green-200 bg-green-50 p-3">
                  <div className="text-sm font-semibold text-green-700 mb-1">
                    Added Servicers
                  </div>
                  <ul className="list-disc list-inside text-sm">
                    {pendingChangesSummary.addedServicers.map((name, idx) => (
                      <li key={`added-servicer-${idx}`}>{name}</li>
                    ))}
                  </ul>
                </div>
              )}

              {pendingChangesSummary.removedServicers.length > 0 && (
                <div className="rounded border border-red-200 bg-red-50 p-3">
                  <div className="text-sm font-semibold text-red-700 mb-1">
                    Removed Servicers
                  </div>
                  <ul className="list-disc list-inside text-sm">
                    {pendingChangesSummary.removedServicers.map((name, idx) => (
                      <li key={`removed-servicer-${idx}`}>{name}</li>
                    ))}
                  </ul>
                </div>
              )}

              {pendingChangesSummary.updatedServicers.map((item, idx) => (
                <div
                  key={`updated-servicer-${idx}`}
                  className="rounded border border-gray-200 bg-gray-50 p-3"
                >
                  <div className="text-sm font-semibold mb-2">
                    {item.servicerName}
                  </div>

                  {item.addedCodes.length > 0 && (
                    <div className="mb-2">
                      <div className="text-xs font-semibold text-green-700 mb-1">
                        Added Product Codes
                      </div>
                      <ul className="list-disc list-inside text-sm">
                        {item.addedCodes.map((code, codeIdx) => (
                          <li key={`added-code-${idx}-${codeIdx}`}>{code}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {item.removedCodes.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-red-700 mb-1">
                        Removed Product Codes
                      </div>
                      <ul className="list-disc list-inside text-sm">
                        {item.removedCodes.map((code, codeIdx) => (
                          <li key={`removed-code-${idx}-${codeIdx}`}>{code}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Sidebar>
    </>
  );
};

export default VIewInvestorProdCodes;
